import React, { useState, useEffect, useCallback } from 'react';
import {
  MegaphoneIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

const TARGET_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active Subscribers' },
  { value: 'free', label: 'Free Users' },
];

const TARGET_COLORS = {
  all: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  free: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const AdminBroadcasts = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newTarget, setNewTarget] = useState('all');
  const [newImage, setNewImage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const messageRef = React.useRef(null);

  const fetchBroadcasts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get(`/api/auth/admin/broadcasts/?page=${page}`);
      setBroadcasts(data.broadcasts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchBroadcasts(); }, [fetchBroadcasts]);

  const insertFormatting = (before, after = '') => {
    const textarea = messageRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    const newText = newMessage.substring(0, start) + before + selectedText + after + newMessage.substring(end);
    
    setNewMessage(newText);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || !newMessage.trim()) {
      alert('Please fill in both title and message');
      return;
    }
    try {
      setCreating(true);
      const formData = new FormData();
      formData.append('title', newTitle.trim());
      formData.append('message', newMessage.trim());
      formData.append('target_audience', newTarget);
      if (newImage) {
        formData.append('image', newImage);
      }
      
      const response = await apiClient.post('/api/auth/admin/broadcasts/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response) {
        setShowCreate(false);
        setNewTitle('');
        setNewMessage('');
        setNewTarget('all');
        setNewImage(null);
        fetchBroadcasts();
      }
    } catch (err) {
      console.error('Failed to create broadcast:', err);
      alert(err.response?.data?.error || 'Failed to create broadcast. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await apiClient.patch(`/api/auth/admin/broadcasts/${b.id}/`, { is_active: !b.is_active });
      setBroadcasts(prev => prev.map(x => x.id === b.id ? { ...x, is_active: !x.is_active } : x));
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete broadcast "${b.title}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/auth/admin/broadcasts/${b.id}/`);
      fetchBroadcasts();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MegaphoneIcon className="h-5 w-5 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Broadcast Messages</h3>
          <span className="text-xs text-gray-400">({total})</span>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <PlusIcon className="h-3.5 w-3.5" /> New Broadcast
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4 space-y-3">
          <input
            value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Broadcast title..."
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <div>
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <button
                type="button"
                onClick={() => insertFormatting('**', '**')}
                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('*', '*')}
                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 italic"
                title="Italic"
              >
                I
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('\n• ')}
                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                title="Bullet point"
              >
                •
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('\n1. ')}
                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                title="Numbered list"
              >
                1.
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
              >
                Preview
              </button>
            </div>
            <textarea
              ref={messageRef}
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Write your broadcast message...\n\nFormatting:\n**bold** for bold text\n*italic* for italic text\n• Bullet points\n1. Numbered lists"
              rows={6}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setNewImage(e.target.files[0])}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
            />
            {newImage && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Selected: {newImage.name}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <select value={newTarget} onChange={e => setNewTarget(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              {TARGET_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div className="flex-1" />
            <button onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg">Cancel</button>
            <button onClick={handleCreate} disabled={creating || !newTitle.trim() || !newMessage.trim()}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {creating ? 'Creating...' : 'Create & Publish'}
            </button>
          </div>
        </div>
      )}

      {/* Broadcasts List */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading broadcasts...</div>
      ) : broadcasts.length === 0 ? (
        <div className="text-center py-16">
          <MegaphoneIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No broadcasts yet. Create one to announce updates to your users.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {broadcasts.map(b => (
            <div key={b.id} className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${
              !b.is_active ? 'opacity-50' : ''
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{b.title}</h4>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${TARGET_COLORS[b.target_audience] || TARGET_COLORS.all}`}>
                      {TARGET_OPTIONS.find(o => o.value === b.target_audience)?.label || b.target_audience}
                    </span>
                    {!b.is_active && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap line-clamp-3">{b.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>{new Date(b.created_at).toLocaleDateString()}</span>
                    {b.created_by && <span>by {b.created_by}</span>}
                    <span className="flex items-center gap-0.5">
                      <EyeIcon className="h-3 w-3" /> {b.dismissals_count} seen
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(b)}
                    className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                      b.is_active
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                    {b.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => handleDelete(b)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{total} broadcasts</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-30">
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="text-sm px-3 text-gray-600 dark:text-gray-300">{page}/{pages}</span>
            <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-30">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{newTitle || 'Untitled'}</h4>
              
              {newImage && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={URL.createObjectURL(newImage)} 
                    alt="Preview"
                    className="w-full h-auto object-cover max-h-64"
                  />
                </div>
              )}
              
              <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {renderFormattedText(newMessage || 'No message')}
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to render formatted text
const renderFormattedText = (text) => {
  if (!text) return null;
  
  // Split by lines to handle formatting
  const lines = text.split('\n');
  
  return lines.map((line, index) => {
    // Process bold and italic
    const parts = [];
    let lastIndex = 0;
    
    // Match **bold** and *italic*
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = regex.exec(line)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index));
      }
      
      const matched = match[0];
      if (matched.startsWith('**') && matched.endsWith('**')) {
        // Bold
        parts.push(<strong key={`${index}-${match.index}`}>{matched.slice(2, -2)}</strong>);
      } else if (matched.startsWith('*') && matched.endsWith('*')) {
        // Italic
        parts.push(<em key={`${index}-${match.index}`}>{matched.slice(1, -1)}</em>);
      }
      
      lastIndex = match.index + matched.length;
    }
    
    // Add remaining text
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }
    
    // Return formatted line
    return (
      <div key={index}>
        {parts.length > 0 ? parts : line}
      </div>
    );
  });
};

export default AdminBroadcasts;
